/* ==================================================================
   Pāw-cipes — "Log your order for Pāw Coins" form (coins = Shopify store credit).
   Client-side validation + JSON POST to the Ki webhook, with the
   existing-account confirmation handshake.

   Contract (Ki -> form), JSON body with a `status`:
     created            -> new account + claim logged (success)
     linked             -> logged to existing account after confirm (success)
     pending            -> logged, verification pending (success)
     account_exists     -> ask the customer to confirm, then resubmit
     duplicate          -> this order was already logged (info)
     invalid            -> { errors: [...] } validation/consent failure
     error / other      -> generic failure
   This section is independent of the calculator and never blocks it.
   ================================================================== */
(function () {
  'use strict';

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var PHONE_RE = /^[0-9+()\-\s]{7,20}$/;

  function field(form, name) { return form.querySelector('[name="' + name + '"]'); }
  function statusBox(root) { return root.querySelector('[data-paw-form="status"]'); }

  function setStatus(root, type, message) {
    var box = statusBox(root);
    if (!box) { return; }
    box.hidden = false;
    box.className = 'paw-points__status paw-points__status--' + type;
    box.textContent = message;
  }

  function clearStatus(root) {
    var box = statusBox(root);
    if (box) { box.hidden = true; box.textContent = ''; box.className = 'paw-points__status'; }
  }

  // Notice + inline confirm button (used for account_exists).
  function renderConfirm(root, message, label, onConfirm) {
    var box = statusBox(root);
    if (!box) { return; }
    box.hidden = false;
    box.className = 'paw-points__status paw-points__status--notice';
    box.textContent = '';
    var p = document.createElement('p');
    p.style.margin = '0 0 10px';
    p.textContent = message;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'button paw-points__confirm';
    var span = document.createElement('span');
    span.textContent = label;
    btn.appendChild(span);
    btn.addEventListener('click', onConfirm);
    box.appendChild(p);
    box.appendChild(btn);
  }

  function validate(form) {
    var errors = [];
    if (!field(form, 'platform').value) { errors.push('Please choose where you ordered.'); }
    if (!field(form, 'platform_username').value.trim()) { errors.push('Please enter your platform username.'); }
    if (!field(form, 'order_id').value.trim()) { errors.push('Please enter your order ID.'); }
    if (!PHONE_RE.test(field(form, 'phone').value.trim())) { errors.push('Please enter a valid phone number.'); }
    if (!EMAIL_RE.test(field(form, 'email').value.trim())) { errors.push('Please enter a valid email address.'); }
    if (!field(form, 'consent').checked) { errors.push('Please agree to the data-use consent to continue.'); }
    return errors;
  }

  // data-consent-version sits on the panel that wraps the <form>, not on the form itself
  function consentVersion(form) {
    var holder = form.closest('[data-consent-version]');
    return (holder && holder.getAttribute('data-consent-version')) || '1';
  }

  function payload(form, confirmExisting) {
    return {
      platform: field(form, 'platform').value,
      platform_username: field(form, 'platform_username').value.trim(),
      order_id: field(form, 'order_id').value.trim(),
      phone: field(form, 'phone').value.trim(),
      email: field(form, 'email').value.trim(),
      consent: true,
      consent_text_version: consentVersion(form),
      confirm_existing: !!confirmExisting,
      source: 'feeding-page',
      submitted_at: new Date().toISOString()
    };
  }

  function setLoading(btn, on) {
    if (!btn) { return; }
    if (on) { btn.setAttribute('loading', 'true'); btn.setAttribute('aria-busy', 'true'); btn.disabled = true; }
    else { btn.removeAttribute('loading'); btn.removeAttribute('aria-busy'); btn.disabled = false; }
  }

  var SUCCESS = 'Thanks! Your order is logged. We’ll verify it against your marketplace order and add your Pāw Coins (store credit) after the delivery window — we’ll email you.';

  function handle(root, form, webhook, btn, data) {
    var status = (data && data.status) || 'error';
    switch (status) {
      case 'created':
      case 'linked':
      case 'pending':
      case 'pending_verification':
        form.reset();
        setStatus(root, 'success', (data && data.message) || SUCCESS);
        break;
      case 'account_exists':
        renderConfirm(
          root,
          (data && data.message) || 'An account with this email already exists. Log this order to that account?',
          'Yes, log it to my account',
          function () { submit(root, form, webhook, btn, true); }
        );
        break;
      case 'duplicate':
        setStatus(root, 'notice', (data && data.message) || 'This order has already been logged.');
        break;
      case 'invalid':
        setStatus(root, 'error', (data && data.errors && data.errors[0]) || 'Please check your details and try again.');
        break;
      default:
        setStatus(root, 'error', (data && data.message) || 'Sorry, we couldn’t submit that just now. Please try again.');
    }
  }

  function submit(root, form, webhook, btn, confirmExisting) {
    if (!confirmExisting) {
      var errors = validate(form);
      if (errors.length) { setStatus(root, 'error', errors[0]); return; }
    }
    if (!webhook) {
      setStatus(root, 'error', 'Pāw Coins logging isn’t configured yet. Please try again later.');
      if (window.console) { console.warn('[paw-points] missing webhook_url setting'); }
      return;
    }
    clearStatus(root);
    setLoading(btn, true);
    fetch(webhook, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload(form, confirmExisting))
    })
      .then(function (res) {
        return res.json().catch(function () { return { status: res.ok ? 'created' : 'error' }; });
      })
      .then(function (data) { handle(root, form, webhook, btn, data); })
      .catch(function (err) {
        setStatus(root, 'error', 'Sorry, we couldn’t reach the server. Please check your connection and try again.');
        if (window.console) { console.error('[paw-points] submit failed', err); }
      })
      .then(function () { setLoading(btn, false); });
  }

  function init(root) {
    var form = root.querySelector('form');
    if (!form) { return; }
    var webhook = root.getAttribute('data-webhook-url') || '';
    var btn = form.querySelector('[type="submit"]');
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      submit(root, form, webhook, btn, false);
    });
  }

  function boot() {
    document.querySelectorAll('[data-paw-form="points"]').forEach(init);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
